import { useState, useEffect, useRef, useCallback } from 'react';
import { HubShell, type HubTab } from '@/components/common/HubShell';
import { useNavigate } from 'react-router-dom';
import {
  Video, VideoOff, Users, Clock, Calendar, Download, Play, Filter,
  Plus, Search, ExternalLink, Loader2, Settings, CircleDot, PhoneOff,
  ArrowUpRight, RefreshCw, Trash2, ChevronDown, Copy, Link, Info,
  Lock, Check, X, UserPlus, Mail, FileText, MessageCircle, Share2
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@easyflow/ui-frontend/components/ui/dialog';
import { Tabs, TabsContent } from '@easyflow/ui-frontend/components/ui/tabs';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import ShareVideoLinkModal from '@/components/video/ShareVideoLinkModal';

type VideoTabKey = 'rooms' | 'settings';

interface VideoRoom {
  id: string;
  title?: string;
  status?: string;
  room_type?: string;
  access_type?: string;
  created_at?: string;
  started_at?: string;
  ended_at?: string;
  scheduled_at?: string;
  duration_seconds?: number;
  total_participants?: number;
  max_participants?: number;
  creator_name?: string;
  meeting_link?: string;
  guest_link?: string;
  room_sid?: string;
  lead_id?: string;
  appointment_id?: string;
  admission_required?: boolean;
  record_on_connect?: boolean;
  has_recording?: number;
  invitation_counts?: { total?: number; accepted?: number; declined?: number; pending?: number };
  [key: string]: unknown;
}

interface PendingInvitation {
  id?: string;
  roomId: string;
  roomTitle?: string;
  title?: string;
  scheduled_at?: string;
  inviter_name?: string;
  [key: string]: unknown;
}

interface RoomInvitation {
  id?: string;
  user_id?: string;
  user_name?: string;
  email?: string;
  status?: string;
  responded_at?: string;
  [key: string]: unknown;
}

interface VideoSettings {
  default_room_type: string;
  auto_record: boolean;
  max_duration_minutes: number;
  max_participants: number;
  noise_cancellation_enabled: boolean;
  recording_retention_days: number;
  [key: string]: unknown;
}

const VIDEO_TABS: readonly HubTab<VideoTabKey>[] = [
  { key: 'rooms', label: 'Storico Call', mobileLabel: 'Storico', icon: Video },
  { key: 'settings', label: 'Impostazioni', mobileLabel: 'Impostazioni', icon: Settings },
];

function ParticipantMultiSelect({ users, selectedIds, onChange, required }: {
  users: Array<{ id: string; name: string; email: string }>;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(x => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Label>Invita partecipanti {required && <span className="text-red-500">*</span>}</Label>
      <div className="mt-1 flex items-center gap-1.5">
        <div
          className="flex-1 min-h-[38px] flex flex-wrap items-center gap-1 px-2 py-1 rounded-md border border-input bg-background cursor-text"
          onClick={() => setOpen(true)}
        >
          {selectedIds.map(id => {
            const u = users.find(x => x.id === id);
            return u ? (
              <Badge key={id} variant="secondary" className="text-xs flex items-center gap-1 pr-1">
                {u.name}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggle(id); }}
                  className="ml-0.5 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ) : null;
          })}
          {selectedIds.length === 0 && (
            <span className="text-sm text-muted-foreground">Seleziona partecipanti...</span>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-[38px] px-2"
          onClick={() => setOpen(!open)}
        >
          <UserPlus className="w-4 h-4" />
        </Button>
      </div>

      {open && (
        <div className="absolute z-50 bottom-full mb-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-[250px] flex flex-col-reverse">
          <div className="p-2 border-t">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca per nome o email..."
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">Nessun utente trovato</p>
            ) : (
              filtered.map(u => {
                const isSelected = selectedIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors text-left ${isSelected ? 'bg-accent/50' : ''}`}
                    onClick={() => toggle(u.id)}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-primary border-primary' : 'border-input'}`}>
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function VideoRoomsHistoryPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<VideoTabKey>('rooms');
  const [rooms, setRooms] = useState<VideoRoom[]>([]);
  const [totalRooms, setTotalRooms] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [settings, setSettings] = useState<VideoSettings>({
    default_room_type: 'group',
    auto_record: false,
    max_duration_minutes: 240,
    max_participants: 50,
    noise_cancellation_enabled: true,
    recording_retention_days: 30
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const [downloadingRecording, setDownloadingRecording] = useState<string | null>(null);

  const [showNewRoomDialog, setShowNewRoomDialog] = useState(false);
  const [newRoomTitle, setNewRoomTitle] = useState('');
  const [newRoomType, setNewRoomType] = useState('group');
  const [newRoomScheduledAt, setNewRoomScheduledAt] = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);

  const [showCreatedRoomDialog, setShowCreatedRoomDialog] = useState(false);
  const [createdRoomData, setCreatedRoomData] = useState<{ id: string; meetingLink: string; guestLink: string } | null>(null);
  const [expandedRoom, setExpandedRoom] = useState<string | null>(null);
  const [deletingRoom, setDeletingRoom] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showEndConfirm, setShowEndConfirm] = useState<string | null>(null);
  const [endingRoom, setEndingRoom] = useState<string | null>(null);
  const [newRoomAdmissionRequired, setNewRoomAdmissionRequired] = useState(false);

  const [newRoomAccessType, setNewRoomAccessType] = useState<'open' | 'invite_only'>('open');
  const [newRoomInvitedUserIds, setNewRoomInvitedUserIds] = useState<string[]>([]);
  const [tenantUsers, setTenantUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [shareModalRoom, setShareModalRoom] = useState<VideoRoom | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [respondingInvitation, setRespondingInvitation] = useState<string | null>(null);

  const loadRooms = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      params.set('limit', '50');

      const res = await apiClient.get<{ rooms?: VideoRoom[]; total?: number }>(`/video/rooms?${params.toString()}`);
      if (res.success) {
        setRooms(res.data?.rooms || []);
        setTotalRooms(res.data?.total || 0);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore caricamento', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, statusFilter, toast]);

  const loadSettings = async () => {
    try {
      const res = await apiClient.get<{ default_room_type: string; auto_record: boolean; max_duration_minutes: number; max_participants: number; noise_cancellation_enabled: boolean; recording_retention_days: number; [key: string]: unknown }>('/video/settings');
      if (res.success && res.data) setSettings(res.data);
    } catch (e) {}
  };

  const loadTenantUsers = async () => {
    try {
      const res = await apiClient.get<Array<{ id: string; name?: string; email: string }>>('/calendar/users');
      if (res.success && res.data) {
        setTenantUsers(res.data.map((u) => ({ id: u.id, name: u.name || u.email, email: u.email })));
      }
    } catch (e) {}
  };

  const loadPendingInvitations = async () => {
    try {
      const res = await apiClient.get<{ id?: string; roomId: string; roomTitle?: string; title?: string; scheduled_at?: string; inviter_name?: string; [key: string]: unknown }[]>('/video/invitations/pending');
      if (res.success) {
        setPendingInvitations(res.data || []);
      }
    } catch (e) {}
  };

  useEffect(() => {
    loadRooms();
    loadSettings();
    loadTenantUsers();
    loadPendingInvitations();
  }, [statusFilter, dateFrom, dateTo, loadRooms]);

  const respondToInvitation = async (roomId: string, response: 'accepted' | 'declined') => {
    if (!roomId) return;
    setRespondingInvitation(roomId);
    try {
      const res = await apiClient.put(`/video/rooms/${roomId}/invitation/respond`, { response });
      if (res.success) {
        toast({ title: response === 'accepted' ? 'Invito accettato' : 'Invito rifiutato' });
        loadPendingInvitations();
        loadRooms();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setRespondingInvitation(null);
    }
  };

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await apiClient.put<{ default_room_type: string; auto_record: boolean; max_duration_minutes: number; max_participants: number; noise_cancellation_enabled: boolean; recording_retention_days: number; [key: string]: unknown }>('/video/settings', settings);
      if (res.success) {
        toast({ title: 'Impostazioni salvate' });
        // boundary: il backend ritorna le settings aggiornate sul success
        setSettings(res.data as VideoSettings);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const createRoom = async () => {
    if (!newRoomTitle.trim()) return;
    if (newRoomAccessType === 'invite_only' && newRoomInvitedUserIds.length === 0) {
      toast({ title: 'Errore', description: 'Seleziona almeno un partecipante per le room "Solo invitati"', variant: 'destructive' });
      return;
    }
    setCreatingRoom(true);
    try {
      const body: {
        title: string;
        roomType: string;
        admissionRequired: boolean;
        accessType: 'open' | 'invite_only';
        scheduledAt?: string;
        invitedUserIds?: string[];
      } = { title: newRoomTitle.trim(), roomType: newRoomType, admissionRequired: newRoomAdmissionRequired, accessType: newRoomAccessType };
      if (newRoomScheduledAt) body.scheduledAt = new Date(newRoomScheduledAt).toISOString();
      if (newRoomInvitedUserIds.length > 0) body.invitedUserIds = newRoomInvitedUserIds;
      const res = await apiClient.post<{ id: string; meetingLink?: string; guestLink?: string }>('/video/rooms', body);
      if (res.success) {
        setShowNewRoomDialog(false);
        setNewRoomTitle('');
        setNewRoomScheduledAt('');
        setNewRoomAccessType('open');
        setNewRoomInvitedUserIds([]);
        setCreatedRoomData({
          id: res.data?.id ?? '',
          meetingLink: res.data?.meetingLink || '',
          guestLink: res.data?.guestLink || ''
        });
        setShowCreatedRoomDialog(true);
        loadRooms();
        loadPendingInvitations();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setCreatingRoom(false);
    }
  };

  const handlePlayRecording = async (roomId: string) => {
    setDownloadingRecording(roomId);
    try {
      const res = await apiClient.get<Array<{ egress_id?: string; egressId?: string; media_url?: string; mediaUrl?: string }>>(`/video/rooms/${roomId}/recordings`);
      if (res.success && res.data && res.data.length > 0) {
        const recording = res.data[0]!;
        const egressId = recording.egress_id || recording.egressId;
        if (egressId) {
          const dlRes = await apiClient.get<{ url?: string }>(`/video/rooms/${roomId}/composition/${egressId}`);
          if (dlRes.success && dlRes.data?.url) {
            window.open(dlRes.data.url, '_blank');
            return;
          }
        }
        if (recording.media_url || recording.mediaUrl) {
          window.open(recording.media_url || recording.mediaUrl, '_blank');
          return;
        }
        toast({ title: 'Registrazione non ancora disponibile', description: 'La composizione potrebbe essere ancora in elaborazione', variant: 'destructive' });
      } else {
        toast({ title: 'Nessuna registrazione trovata', variant: 'destructive' });
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setDownloadingRecording(null);
    }
  };

  const requestComposition = async (roomId: string) => {
    try {
      const res = await apiClient.post(`/video/rooms/${roomId}/composition`, {});
      if (res.success) {
        toast({ title: 'Composizione avviata', description: 'La registrazione sarà pronta tra qualche minuto' });
        loadRooms();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const formatDuration = (seconds: number | null | undefined) => {
    if (!seconds) return '-';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
  };

  const formatDate = (date: string | undefined) => {
    return new Date(date ?? '').toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const endRoom = async (roomId: string) => {
    setEndingRoom(roomId);
    try {
      const res = await apiClient.post(`/video/rooms/${roomId}/end`, {});
      if (res.success) {
        toast({ title: 'Video call terminata', description: 'Tutti i partecipanti sono stati disconnessi' });
        setShowEndConfirm(null);
        loadRooms();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setEndingRoom(null);
    }
  };

  const deleteRoom = async (roomId: string) => {
    if (!roomId) return;
    setDeletingRoom(roomId);
    try {
      const res = await apiClient.delete(`/video/rooms/${roomId}`);
      if (res.success) {
        toast({ title: 'Room eliminata' });
        setShowDeleteConfirm(null);
        loadRooms();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setDeletingRoom(null);
    }
  };

  const filteredRooms = rooms.filter(r =>
    !searchQuery || r.title?.toLowerCase().includes(searchQuery.toLowerCase()) || r.creator_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );




  return (
    <HubShell
      icon={Video}
      title="Video Call"
      subtitle="gestisci le video call e le registrazioni"
      tabs={VIDEO_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      mainClassName="flex-1 overflow-auto px-6 py-4 space-y-6"
      headerActions={
          <Dialog open={showNewRoomDialog} onOpenChange={setShowNewRoomDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="shrink-0"><Plus className="w-4 h-4 mr-1.5" />Nuova Video Call</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuova Video Call</DialogTitle>
              <DialogDescription>
                Crea una nuova video room. Puoi configurare titolo, tipo, ammissione obbligatoria e registrazione automatica.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Titolo</Label>
                <Input value={newRoomTitle} onChange={e => setNewRoomTitle(e.target.value)} placeholder="Es: Riunione settimanale" className="mt-1" />
              </div>
              <div>
                <Label>Tipo Room</Label>
                <Select value={newRoomType} onValueChange={setNewRoomType}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="group">Group (fino a 50 partecipanti)</SelectItem>
                    <SelectItem value="peer-to-peer">Peer-to-Peer (1:1, costo ridotto)</SelectItem>
                    <SelectItem value="group-small">Group Small (fino a 4)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Programma per (opzionale)</Label>
                <Input type="datetime-local" value={newRoomScheduledAt} onChange={e => setNewRoomScheduledAt(e.target.value)} className="mt-1" />
                {newRoomScheduledAt && (
                  <p className="text-xs text-muted-foreground mt-1">La room Twilio verrà creata solo al momento dell'ingresso.</p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Waiting Room</Label>
                  <p className="text-xs text-muted-foreground">I partecipanti devono essere ammessi dall'host</p>
                </div>
                <Switch checked={newRoomAdmissionRequired} onCheckedChange={setNewRoomAdmissionRequired} />
              </div>
              <div>
                <Label>Accesso</Label>
                <Select value={newRoomAccessType} onValueChange={(v: 'open' | 'invite_only') => setNewRoomAccessType(v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Aperta a tutti</SelectItem>
                    <SelectItem value="invite_only">Solo invitati</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ParticipantMultiSelect
                users={tenantUsers.filter(u => u.id !== user?.id)}
                selectedIds={newRoomInvitedUserIds}
                onChange={setNewRoomInvitedUserIds}
                required={newRoomAccessType === 'invite_only'}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowNewRoomDialog(false)}>Annulla</Button>
                <Button onClick={createRoom} disabled={creatingRoom || !newRoomTitle.trim() || (newRoomAccessType === 'invite_only' && newRoomInvitedUserIds.length === 0)}>
                  {creatingRoom && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  {newRoomScheduledAt ? 'Programma' : 'Crea e Avvia'}
                </Button>
              </div>
            </div>
          </DialogContent>
          </Dialog>
      }
    >
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as VideoTabKey)}>

        <TabsContent value="rooms" className="mt-4 space-y-4">
          {pendingInvitations.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <Mail className="w-4 h-4" />
                I miei inviti ({pendingInvitations.length})
              </h3>
              <div className="grid gap-2">
                {pendingInvitations.map((inv) => (
                  <div key={inv.roomId || inv.id} className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-xl p-4 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm">{inv.roomTitle || inv.title || 'Video Call'}</h4>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        {inv.scheduled_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(inv.scheduled_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {inv.inviter_name && <span>da {inv.inviter_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => respondToInvitation(inv.roomId, 'accepted')}
                        disabled={respondingInvitation === inv.roomId}
                      >
                        {respondingInvitation === inv.roomId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                        Accetta
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => respondToInvitation(inv.roomId, 'declined')}
                        disabled={respondingInvitation === inv.roomId}
                      >
                        {respondingInvitation === inv.roomId ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3 mr-1" />}
                        Rifiuta
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Cerca per titolo o creatore..."
                className="h-9"
              />
            </div>
            <Select value={statusFilter || '__all__'} onValueChange={v => setStatusFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli stati</SelectItem>
                <SelectItem value="scheduled">Programmate</SelectItem>
                <SelectItem value="in-progress">In corso</SelectItem>
                <SelectItem value="completed">Completate</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-[140px]" />
              <span className="text-muted-foreground text-sm">-</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-[140px]" />
            </div>
            <Button variant="outline" size="sm" className="h-9" onClick={loadRooms}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
          ) : filteredRooms.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <VideoOff className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
              <p>Nessuna video call trovata</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredRooms.map(room => (
                <div key={room.id} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-sm transition-shadow">
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <button
                          onClick={() => setExpandedRoom(expandedRoom === room.id ? null : room.id)}
                          className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${room.status === 'in-progress' ? 'bg-green-100 text-green-600 dark:bg-green-950/40 dark:text-green-400' : 'bg-muted text-muted-foreground hover:bg-muted/70'}`}
                        >
                          <ChevronDown className={`w-5 h-5 transition-transform ${expandedRoom === room.id ? 'rotate-180' : ''}`} />
                        </button>
                        <div className="min-w-0">
                          <h3 className="font-medium text-sm truncate">{room.title || 'Video Call'}</h3>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(room.created_at)}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDuration(room.duration_seconds)}</span>
                            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{room.total_participants || 0}</span>
                            {room.creator_name && <span>di {room.creator_name}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${room.status === 'in-progress' ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300' : room.status === 'scheduled' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300' : 'bg-muted text-muted-foreground'}`}>
                          {room.status === 'in-progress' ? 'In corso' : room.status === 'scheduled' ? 'Programmata' : 'Completata'}
                        </span>

                        {room.access_type === 'invite_only' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 flex items-center gap-1">
                            <Lock className="w-3 h-3" />Solo invitati
                          </span>
                        )}

                        {room.invitation_counts && (room.invitation_counts.total ?? 0) > 0 && (
                          <span className="flex items-center gap-1.5 text-xs">
                            {(room.invitation_counts.accepted ?? 0) > 0 && (
                              <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300">{room.invitation_counts.accepted} accettati</span>
                            )}
                            {(room.invitation_counts.declined ?? 0) > 0 && (
                              <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300">{room.invitation_counts.declined} rifiutati</span>
                            )}
                            {(room.invitation_counts.pending ?? 0) > 0 && (
                              <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">{room.invitation_counts.pending} in sospeso</span>
                            )}
                          </span>
                        )}

                        {room.admission_required && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">Waiting Room</span>
                        )}

                        {room.scheduled_at && room.status === 'scheduled' && (
                          <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(room.scheduled_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}

                        {room.meeting_link && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-indigo-600" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(room.meeting_link ?? ""); toast({ title: 'Link meeting copiato!' }); }} title={room.meeting_link}>
                            <Copy className="w-3 h-3 mr-1" />Meeting
                          </Button>
                        )}

                        {room.guest_link && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-purple-600" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(room.guest_link ?? ""); toast({ title: 'Link ospite copiato!' }); }} title={room.guest_link}>
                            <Copy className="w-3 h-3 mr-1" />Ospite
                          </Button>
                        )}

                        {(room.meeting_link || room.guest_link) && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-green-600 hover:text-green-700 hover:bg-green-50" onClick={(e) => { e.stopPropagation(); setShareModalRoom(room); }}>
                            <Share2 className="w-3 h-3 mr-1" />Condividi
                          </Button>
                        )}

                        {room.status === 'in-progress' && (
                          showEndConfirm === room.id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="destructive" size="sm" className="h-7 text-xs text-white" onClick={() => endRoom(room.id)} disabled={endingRoom === room.id}>
                                {endingRoom === room.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Termina'}
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowEndConfirm(null)}>No</Button>
                            </div>
                          ) : (
                            <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 dark:text-red-400 border-red-300 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-700" onClick={() => setShowEndConfirm(room.id)}>
                              <PhoneOff className="w-3 h-3 mr-1" />Termina
                            </Button>
                          )
                        )}

                        {(room.status === 'in-progress' || room.status === 'scheduled') && (
                          <Button size="sm" className="h-7 text-xs" onClick={() => { const slug = window.location.pathname.split('/')[1] || ''; navigate(`/${slug}/video/${room.id}`); }}>
                            <ArrowUpRight className="w-3 h-3 mr-1" />{room.status === 'scheduled' ? 'Avvia' : 'Entra'}
                          </Button>
                        )}

                        {room.status === 'completed' && room.record_on_connect && (
                          <>
                            {(room.has_recording ?? 0) > 0 ? (
                              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handlePlayRecording(room.id)} disabled={downloadingRecording === room.id}>
                                {downloadingRecording === room.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}Registrazione
                              </Button>
                            ) : (
                              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => requestComposition(room.id)}>
                                <Download className="w-3 h-3 mr-1" />Crea Registrazione
                              </Button>
                            )}
                          </>
                        )}

                        {showDeleteConfirm === room.id ? (
                          <div className="flex items-center gap-1">
                            <Button variant="destructive" size="sm" className="h-7 text-xs text-white" onClick={() => deleteRoom(room.id)} disabled={deletingRoom === room.id}>
                              {deletingRoom === room.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Conferma'}
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowDeleteConfirm(null)}>No</Button>
                          </div>
                        ) : (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 dark:text-red-400 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30" onClick={() => setShowDeleteConfirm(room.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedRoom === room.id && (
                    <div className="border-t border-border bg-muted/50 px-4 py-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <span className="text-muted-foreground block">Tipo Room</span>
                          <span className="font-medium">{room.room_type === 'group' ? 'Group' : room.room_type === 'peer-to-peer' ? 'Peer-to-Peer' : 'Group Small'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Creata</span>
                          <span className="font-medium">{formatDate(room.created_at)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Inizio</span>
                          <span className="font-medium">{room.started_at ? formatDate(room.started_at) : '-'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Fine</span>
                          <span className="font-medium">{room.ended_at ? formatDate(room.ended_at) : '-'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Durata</span>
                          <span className="font-medium">{formatDuration(room.duration_seconds)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Creatore</span>
                          <span className="font-medium">{room.creator_name || '-'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Partecipanti</span>
                          <span className="font-medium">{room.total_participants || 0}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Registrazione</span>
                          <span className="font-medium">{room.record_on_connect ? 'Sì' : 'No'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Waiting Room</span>
                          <span className="font-medium">{room.admission_required ? 'Attiva' : 'No'}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Max Partecipanti</span>
                          <span className="font-medium">{room.max_participants || '-'}</span>
                        </div>
                        {room.room_sid && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground block">Room SID</span>
                            <span className="font-mono text-[10px]">{room.room_sid}</span>
                          </div>
                        )}
                        {room.meeting_link && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground block">Link Meeting</span>
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-[10px] truncate">{room.meeting_link}</span>
                              <button onClick={() => { navigator.clipboard.writeText(room.meeting_link ?? ""); toast({ title: 'Copiato!' }); }} className="text-indigo-500 dark:text-indigo-400 hover:text-indigo-700"><Copy className="w-3 h-3" /></button>
                            </div>
                          </div>
                        )}
                        {room.guest_link && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground block">Link Ospite</span>
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-[10px] truncate">{room.guest_link}</span>
                              <button onClick={() => { navigator.clipboard.writeText(room.guest_link ?? ""); toast({ title: 'Copiato!' }); }} className="text-purple-500 dark:text-purple-400 hover:text-purple-700"><Copy className="w-3 h-3" /></button>
                            </div>
                          </div>
                        )}
                        {room.lead_id && (
                          <div>
                            <span className="text-muted-foreground block">Lead CRM</span>
                            <Button variant="ghost" size="sm" className="h-5 text-xs text-blue-600 p-0" onClick={() => { const slug = window.location.pathname.split('/')[1] || ''; navigate(`/${slug}/crm?leadId=${room.lead_id}`); }}>
                              Vai al Lead
                            </Button>
                          </div>
                        )}
                        {room.appointment_id && (
                          <div>
                            <span className="text-muted-foreground block">Appuntamento</span>
                            <Button variant="ghost" size="sm" className="h-5 text-xs text-purple-600 p-0" onClick={() => { const slug = window.location.pathname.split('/')[1] || ''; navigate(`/${slug}/calendar`); }}>
                              Vai al Calendario
                            </Button>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground block">Accesso</span>
                          <span className="font-medium">{room.access_type === 'invite_only' ? 'Solo invitati' : 'Aperta a tutti'}</span>
                        </div>
                      </div>
                      {room.invitation_counts && (room.invitation_counts.total ?? 0) > 0 && (
                        <RoomInvitationsDetail roomId={room.id} />
                      )}
                      <RoomTranscriptionSection roomId={room.id} status={room.status} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {totalRooms > 0 && (
            <p className="text-xs text-muted-foreground text-center">{filteredRooms.length} di {totalRooms} video call</p>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl p-6 space-y-5">
              <h3 className="font-semibold text-lg">Impostazioni Video Call</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo Room predefinito</Label>
                  <Select value={settings.default_room_type} onValueChange={v => setSettings((s) => ({ ...s, default_room_type: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="group">Group</SelectItem>
                      <SelectItem value="peer-to-peer">Peer-to-Peer</SelectItem>
                      <SelectItem value="group-small">Group Small</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max partecipanti</Label>
                  <Input
                    type="number"
                    value={settings.max_participants}
                    onChange={e => setSettings((s) => ({ ...s, max_participants: parseInt(e.target.value) || 50 }))}
                    className="mt-1"
                    min={2} max={50}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Durata massima (minuti)</Label>
                  <Input
                    type="number"
                    value={settings.max_duration_minutes}
                    onChange={e => setSettings((s) => ({ ...s, max_duration_minutes: parseInt(e.target.value) || 240 }))}
                    className="mt-1"
                    min={5} max={1440}
                  />
                </div>
                <div>
                  <Label>Conservazione registrazioni (giorni)</Label>
                  <Input
                    type="number"
                    value={settings.recording_retention_days}
                    onChange={e => setSettings((s) => ({ ...s, recording_retention_days: parseInt(e.target.value) || 30 }))}
                    className="mt-1"
                    min={1} max={365}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Registrazione automatica</Label>
                    <p className="text-xs text-muted-foreground">Registra automaticamente tutte le video call</p>
                  </div>
                  <Switch
                    checked={settings.auto_record}
                    onCheckedChange={v => setSettings((s) => ({ ...s, auto_record: v }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Noise Cancellation</Label>
                    <p className="text-xs text-muted-foreground">Soppressione rumore di fondo (Krisp)</p>
                  </div>
                  <Switch
                    checked={settings.noise_cancellation_enabled}
                    onCheckedChange={v => setSettings((s) => ({ ...s, noise_cancellation_enabled: v }))}
                  />
                </div>
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-lg">
                  <Info className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    La funzionalità Noise Cancellation richiede una licenza Krisp separata. L'impostazione viene salvata, ma la soppressione del rumore sarà attiva solo con una licenza Krisp valida configurata.
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={saveSettings} disabled={settingsLoading}>
                  {settingsLoading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Salva Impostazioni
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showCreatedRoomDialog} onOpenChange={setShowCreatedRoomDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-green-500" />
              Video Call Creata
            </DialogTitle>
            <DialogDescription>
              La video call è stata creata correttamente. Copia i link per condividerli con i partecipanti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">La video call è stata creata. Copia i link per condividerli via WhatsApp, email o altri canali.</p>

            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                  <Link className="w-3 h-3" /> Link Meeting (utenti interni)
                </Label>
                <div className="flex gap-2">
                  <Input value={createdRoomData?.meetingLink || ''} readOnly className="text-xs" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(createdRoomData?.meetingLink || ''); toast({ title: 'Link meeting copiato!' }); }}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                  <ExternalLink className="w-3 h-3" /> Link Ospite (partecipanti esterni)
                </Label>
                <div className="flex gap-2">
                  <Input value={createdRoomData?.guestLink || ''} readOnly className="text-xs" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(createdRoomData?.guestLink || ''); toast({ title: 'Link ospite copiato!' }); }}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreatedRoomDialog(false)}>Chiudi</Button>
              <Button onClick={() => { setShowCreatedRoomDialog(false); const slug = window.location.pathname.split('/')[1] || ''; navigate(`/${slug}/video/${createdRoomData?.id}`); }}>
                <ArrowUpRight className="w-4 h-4 mr-1" /> Entra nella Call
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ShareVideoLinkModal
        open={!!shareModalRoom}
        onClose={() => setShareModalRoom(null)}
        // boundary: la modale è aperta solo per room con link presenti (bottone "Condividi")
        room={shareModalRoom as { id: string; title: string; meeting_link: string; guest_link: string } | null}
      />
    </HubShell>
  );
}

interface TranscriptionSegmentDTO {
  timestamp: string;
  speakerIdentity: string;
  speakerName: string;
  text: string;
}

interface TranscriptionExistsResponse {
  hasTranscription?: boolean;
}

interface SummaryGetData {
  text: string;
  generated_at: string;
}

interface SummaryGenerateData {
  summary: string;
  generatedAt: string;
}

function RoomTranscriptionSection({ roomId, status }: { roomId: string; status: string | undefined }) {
  const [transcription, setTranscription] = useState<TranscriptionSegmentDTO[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [checked, setChecked] = useState(false);
  const [hasTranscription, setHasTranscription] = useState(false);
  const [summary, setSummary] = useState<SummaryGetData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (status !== 'completed') return;
    let cancelled = false;
    apiClient.get(`/video/rooms/${roomId}/summary`).then((res) => {
      if (cancelled) return;
      const data = (res as { data?: SummaryGetData | null }).data;
      if (res.success && data) setSummary(data);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [roomId, status]);

  const downloadFile = async (format: 'vtt' | 'srt') => {
    try {
      const blob = await apiClient.getBlob(`/video/rooms/${roomId}/transcript.${format}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trascrizione-${roomId.slice(0, 8)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Errore download', description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  const generateSummary = async (force = false) => {
    setSummaryLoading(true);
    try {
      const res = await apiClient.post(`/video/rooms/${roomId}/summary`, { force });
      if (!res.success) throw new Error(res.error || 'Errore generazione');
      const data = (res as { data?: SummaryGenerateData }).data;
      if (data) setSummary({ text: data.summary, generated_at: data.generatedAt });
      toast({ title: 'Riassunto generato' });
    } catch (err) {
      toast({ title: 'Errore', description: err instanceof Error ? err.message : '', variant: 'destructive' });
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (status !== 'completed') return;
    const check = async () => {
      try {
        const res = await apiClient.get<TranscriptionExistsResponse>(`/video/rooms/${roomId}/transcription/exists`);
        const flag = (res as TranscriptionExistsResponse).hasTranscription;
        if (res.success && flag) {
          setHasTranscription(true);
        }
      } catch {}
      setChecked(true);
    };
    check();
  }, [roomId, status]);

  if (!checked || !hasTranscription) return null;

  const loadTranscription = async () => {
    if (transcription) {
      setExpanded(!expanded);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get<TranscriptionSegmentDTO[]>(`/video/rooms/${roomId}/transcription`);
      if (res.success) {
        setTranscription((res.data as TranscriptionSegmentDTO[] | undefined) || []);
        setExpanded(true);
      }
    } catch {}
    setLoading(false);
  };

  const copyAll = () => {
    if (!transcription) return;
    const text = transcription.map((t) =>
      `[${new Date(t.timestamp).toLocaleTimeString('it-IT')}] ${t.speakerName}: ${t.text}`
    ).join('\n');
    navigator.clipboard.writeText(text);
    toast({ title: 'Trascrizione copiata!' });
  };

  const downloadTxt = () => {
    if (!transcription) return;
    const text = transcription.map((t) =>
      `[${new Date(t.timestamp).toLocaleTimeString('it-IT')}] ${t.speakerName}: ${t.text}`
    ).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trascrizione-${roomId.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const speakerColors: Record<string, string> = {};
  const colorPalette = ['text-blue-600', 'text-green-600', 'text-purple-600', 'text-amber-600', 'text-pink-600', 'text-cyan-600'];
  let colorIdx = 0;
  const getColor = (identity: string) => {
    if (!speakerColors[identity]) {
      speakerColors[identity] = colorPalette[colorIdx % colorPalette.length] ?? '';
      colorIdx++;
    }
    return speakerColors[identity];
  };

  return (
    <div className="mt-3 border-t pt-3">
      <button
        onClick={loadTranscription}
        className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-indigo-600 transition-colors"
      >
        <FileText className="w-3.5 h-3.5" />
        Trascrizione
        {loading && <Loader2 className="w-3 h-3 animate-spin" />}
        {transcription && <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />}
      </button>
      {expanded && transcription && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">{transcription.length} segmenti</span>
            <button onClick={copyAll} className="text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5">
              <Copy className="w-3 h-3" />Copia
            </button>
            <button onClick={downloadTxt} className="text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5">
              <Download className="w-3 h-3" />TXT
            </button>
            <button onClick={() => downloadFile('vtt')} className="text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5">
              <Download className="w-3 h-3" />VTT
            </button>
            <button onClick={() => downloadFile('srt')} className="text-[10px] text-indigo-500 hover:text-indigo-700 flex items-center gap-0.5">
              <Download className="w-3 h-3" />SRT
            </button>
            <button
              onClick={() => generateSummary(!!summary)}
              disabled={summaryLoading}
              className="text-[10px] text-purple-600 hover:text-purple-700 flex items-center gap-0.5 disabled:opacity-50"
            >
              {summaryLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
              {summary ? 'Rigenera riassunto' : 'Genera riassunto AI'}
            </button>
          </div>
          {summary && (
            <div className="bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-xs whitespace-pre-wrap text-foreground/90">
              <div className="font-semibold text-purple-700 dark:text-purple-300 mb-1 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Riassunto AI
                <span className="text-[10px] text-muted-foreground font-normal ml-auto">
                  {new Date(summary.generated_at).toLocaleString('it-IT')}
                </span>
              </div>
              {summary.text}
            </div>
          )}
          <div className="bg-card border border-border rounded-lg p-3 max-h-64 overflow-y-auto space-y-1">
            {transcription.map((t, i) => (
              <div key={i} className="text-xs flex gap-1.5">
                <span className="text-muted-foreground font-mono flex-shrink-0">
                  {new Date(t.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
                <span className={`font-medium flex-shrink-0 ${getColor(t.speakerIdentity)}`}>
                  {t.speakerName}:
                </span>
                <span className="text-foreground/80">{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RoomInvitationsDetail({ roomId }: { roomId: string }) {
  const [invitations, setInvitations] = useState<RoomInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get<{ id?: string; user_id?: string; user_name?: string; email?: string; status?: string; responded_at?: string; [key: string]: unknown }[]>(`/video/rooms/${roomId}/invitations`);
        if (res.success) {
          setInvitations(res.data || []);
        }
      } catch (e) {}
      setLoading(false);
    };
    load();
  }, [roomId]);

  if (loading) return <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" />Caricamento invitati...</div>;
  if (invitations.length === 0) return null;

  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    accepted: { bg: 'bg-green-100 dark:bg-green-950/40', text: 'text-green-700 dark:text-green-300', label: 'Accettato' },
    declined: { bg: 'bg-red-100 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-300', label: 'Rifiutato' },
    pending: { bg: 'bg-amber-100 dark:bg-amber-950/40', text: 'text-amber-700 dark:text-amber-300', label: 'In sospeso' }
  };

  return (
    <div className="mt-3 border-t pt-3">
      <h4 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
        <UserPlus className="w-3 h-3" /> Invitati ({invitations.length})
      </h4>
      <div className="space-y-1">
        {invitations.map((inv) => {
          const sc = statusConfig[inv.status ?? 'pending'] || statusConfig.pending;
          return (
            <div key={inv.user_id || inv.id} className="flex items-center justify-between text-xs">
              <span className="text-foreground/80">{inv.user_name || inv.email || 'Utente'}</span>
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded-full ${sc?.bg ?? ''} ${sc?.text ?? ''}`}>{sc?.label ?? ''}</span>
                {inv.responded_at && (
                  <span className="text-muted-foreground">
                    {new Date(inv.responded_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
